/**
 * Noticing that the display's pixel ratio has changed.
 *
 * A canvas holds a backing store sized for the ratio it was built at, while
 * the drawing library clears it each frame over an area worked out from the
 * ratio in force at that moment. While the two agree this is invisible. When
 * the ratio changes under a canvas already built, the clear falls short of the
 * store and leaves a band of the canvas that is never wiped, where each
 * frame's strokes pile onto the last one's until that band is a dark, thick
 * smear. Nothing else catches it, because the element's own size need not have
 * changed at all.
 *
 * The ratio changes more often than it sounds: browser zoom moves it, as does
 * dragging a window to a display of another density, or leaving a device
 * emulator.
 *
 * There is no event for the ratio itself, so this asks instead whether the
 * ratio it last saw still holds. A media query names one ratio and reports
 * when that answer turns false, so each change needs a fresh query for the
 * ratio just arrived at, which is why this re-arms itself.
 */

/**
 * Call `onChange` whenever the display's pixel ratio changes, until the
 * returned function is called. Returns a function that does nothing where the
 * browser has no media queries to ask.
 */
export function watchPixelRatio(onChange: () => void): () => void {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return () => {};
  }

  let query: MediaQueryList | null = null;

  const listen = () => {
    query = window.matchMedia(`(resolution: ${window.devicePixelRatio}dppx)`);
    query.addEventListener("change", answered);
  };

  const answered = () => {
    stop();
    listen();
    onChange();
  };

  const stop = () => {
    query?.removeEventListener("change", answered);
    query = null;
  };

  listen();
  return stop;
}
